import React from "react";

/**
 * Liventy logo mark — usa o SVG/PNG oficial do "L" enviado pelo usuário,
 * embutido em um fundo app-icon premium com glassmorphism, gradiente suave,
 * glow rosa/roxo e sombra moderna.
 *
 * Sem reinterpretar o símbolo: a imagem é renderizada exatamente como recebida.
 *
 * Props:
 *  - size: lado total do tile em px. Default 32.
 *  - bare: se true, renderiza apenas o glyph sem o fundo app-icon.
 *          Útil quando o componente está sobre um header gradient ou imagem.
 *  - className: classes extras no wrapper.
 *  - title: aria-label.
 */
export function LiventyLogoMark({ size = 32, bare = false, className = "", title = "Liventy" }) {
  // Padding interno proporcional para o L respirar dentro do tile
  const innerPad = Math.max(2, Math.round(size * 0.18));
  const radius = Math.round(size * 0.235); // ~iOS app-icon (22.4%)

  if (bare) {
    return (
      <img
        src="/brand/liventy-l-mark.webp"
        alt={title}
        width={size}
        height={size}
        style={{ display: "block", objectFit: "contain" }}
        className={className}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={title}
      title={title}
      className={`liventy-icon ${className}`}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: radius,
        // Off-white premium + sutil radial glow rosa→roxo no fundo
        background:
          "radial-gradient(120% 110% at 30% 20%, rgba(255,179,71,0.18) 0%, rgba(255,77,141,0.18) 35%, rgba(107,61,246,0.20) 70%, rgba(255,255,255,0) 100%), linear-gradient(160deg, #FBFAFF 0%, #F1ECFF 60%, #E9E0FF 100%)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.75), inset 0 -8px 14px -8px rgba(107,61,246,0.18), 0 10px 30px -10px rgba(107,61,246,0.35), 0 4px 12px -6px rgba(255,77,141,0.18)",
        overflow: "hidden",
        // glassmorphism (suportado em browsers modernos)
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        flexShrink: 0,
      }}
    >
      {/* Borda interna translucida para reforçar a vidraça */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          border: "1px solid rgba(255,255,255,0.55)",
          pointerEvents: "none",
        }}
      />
      {/* Glow no canto superior — iluminação difusa */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "-30%",
          left: "-20%",
          width: "85%",
          height: "85%",
          background:
            "radial-gradient(50% 50% at 50% 50%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 70%)",
          pointerEvents: "none",
        }}
      />
      <img
        src="/brand/liventy-l-mark.webp"
        alt=""
        aria-hidden="true"
        width={size - innerPad * 2}
        height={size - innerPad * 2}
        style={{
          position: "relative",
          display: "block",
          objectFit: "contain",
          filter: "drop-shadow(0 4px 8px rgba(107,61,246,0.25))",
        }}
      />
    </span>
  );
}

/**
 * Liventy wordmark — usa a IMAGEM OFICIAL da marca (PNG transparente).
 * 4 variantes oficiais enviadas pelo usuário:
 *  - 'gradient': roxo→rosa→laranja (default — uso na hero / fundos claros)
 *  - 'dark':     navy escuro #141023 (headers / topbars / sidebars)
 *  - 'white':    branca (fundos coloridos / escuros)
 *  - 'gray':     cinza (uso secundário / disabled)
 *
 * Props:
 *  - height: altura do wordmark em px. Default 22.
 *  - variant: 'gradient' | 'dark' | 'white' | 'gray'. Default 'gradient'.
 *  - className: classes extras.
 *  - title: aria-label / alt.
 */
export function LiventyWordmark({ height = 22, variant = "gradient", className = "", title = "Liventy" }) {
  const src = `/brand/liventy-wordmark-${variant}.webp`;
  return (
    <img
      src={src}
      alt={title}
      style={{
        height: `${height}px`,
        width: "auto",
        display: "block",
        objectFit: "contain",
      }}
      className={className}
      draggable={false}
    />
  );
}

/**
 * Liventy lockup completo — mark + wordmark alinhados.
 */
export function LiventyLogo({ size = 32, color = "#141023", showWordmark = true, showR = false, bare = false, className = "", testid }) {
  const wordSize = Math.round(size * 0.58);
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`} data-testid={testid}>
      <LiventyLogoMark size={size} bare={bare} />
      {showWordmark && <LiventyWordmark size={wordSize} color={color} showR={showR} />}
    </div>
  );
}

export default LiventyLogo;
